import { SxProps, Typography } from "@mui/material";
import {
  useMemo,
  useRef,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { Box, Icon, Link } from "../../../common/components";
import { useTimeSince } from "../../../common/hooks/useTimeSince";
import DesignService from "../../../service/DesignService";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { DesignState, ProductLineType, Project } from "../../../types";
import { ProductLinesToColor, ROUTES } from "../../../constant/common.constant";
import {
  DND_ITEM_TYPES,
  DraggedDesignItem,
} from "../../../constant/dnd.constant";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import DesignCardMenu from "./DesignCardMenu";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../common/hooks/useAuth";
import { LockedDesign } from "./components";
import { useTeam } from "../../../context/Team/useTeam";
import { useProject } from "../../../context/Project/useProject";

export const DESIGN_CARD_MOBILE_MIN_WIDTH = "160px";
export const DESIGN_CARD_MOBILE_MAX_WIDTH = "246px";
export const DESIGN_CARD_DESKTOP_MIN_WIDTH = "200px";
export const DESIGN_CARD_DESKTOP_MAX_WIDTH = "246px";

const useStyles = (
  isFavorite: boolean,
  noNavigate?: boolean,
  maxWidth?: { xs: string; md: string }
) =>
  createStyles({
    designContainer: {
      display: "flex",
      flexDirection: "column",
      maxWidth: {
        xs: maxWidth?.xs ?? DESIGN_CARD_MOBILE_MAX_WIDTH,
        md: maxWidth?.md ?? DESIGN_CARD_DESKTOP_MAX_WIDTH,
      },
      position: "relative",
    },
    imgContainer: {
      minWidth: "100%",
      borderRadius: "12px",
      cursor: noNavigate ? "default" : "pointer",
      position: "relative",
      mb: 1,
      border: `1px solid ${palette.grey.lightGrey}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ":hover": {
        border: `1px solid ${palette.grey.midGrey}`,
      },
      ":active": {
        border: `1px solid ${palette.blue.secondary}`,
      },
      height: 190,
    },
    productLineTag: {
      position: "absolute",
      bottom: "8px",
      left: "8px",
      borderRadius: "8px",
      padding: "4px 8px",
      border: `1px solid ${palette.grey.darkGrey}`,
      display: "flex",
      backgroundColor: palette.white,
    },
    favorite: {
      position: "absolute",
      top: "12px",
      right: "12px",
      "&>span": {
        userSelect: "none",
        color: isFavorite ? palette.misc.favorites : palette.grey.midGrey,
        ":hover": {
          color: isFavorite ? palette.misc.favorites : palette.grey.darkGrey,
        },
      },
    },
    locked: {
      position: "absolute",
      px: "4px",
      py: "2px",
      top: "12px",
      left: "8px",
      display: "flex",
      alignItems: "center",
      gap: spacing.xxsDesktop,
      borderRadius: "100px",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: palette.grey.lightGrey,
      },
    },
    designName: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    text: {
      minWidth: "100%",
      height: "17px",
    },
    img: {
      maxHeight: { xs: "60px", lg: "100px" },
      minHeight: { xs: "60px", lg: "100px" },
      userSelect: "none",
    },
  });

interface Props {
  design: DesignState;
  hideProjectName?: boolean;
  onUpdate?: (hideLoading?: boolean) => Promise<void>;
  setIsLoading?: (isLoading: boolean) => void;
  projectName?: string;
  hideActions?: boolean;
  hideFavorite?: boolean;
  hideLocked?: boolean;
  hideProductLine?: boolean;
  hideTimeSince?: boolean;
  noNavigate?: boolean;
  contentOnHover?: ReactNode;
  imgContainerStyles?: SxProps;
  maxWidth?: { xs: string; md: string };
  disableDrag?: boolean;
}

const DesignCard = ({
  design,
  hideProjectName,
  projectName = "project" in design ? (design.project as Project).name : "",
  onUpdate,
  setIsLoading,
  hideActions,
  hideFavorite,
  hideLocked,
  hideProductLine,
  hideTimeSince,
  noNavigate,
  contentOnHover,
  imgContainerStyles,
  maxWidth,
  disableDrag,
}: Props) => {
  const { t } = useTranslation();
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { timeSince } = useTimeSince(design.updatedAt);
  const { isMobile } = useThemeBreakpoints();
  const [isFavorite, setIsFavorite] = useState(Boolean(design.isFavorite));
  const { refetchProject } = useProject();
  const ref = useRef<HTMLSpanElement>(null);
  const styles = useStyles(isFavorite, noNavigate, maxWidth);
  const { isAdmin } = useAuth();
  const [isHoveringDesignContainer, setHoveringDesignContainer] =
    useState(false);
  const { teamId: teamIdFromUrl } = useTeam();

  const teamId = teamIdFromUrl || design?.project?.teamId || null;

  const [{ isDragging }, drag, preview] = useDrag<
    DraggedDesignItem,
    void,
    { isDragging: boolean }
  >({
    type: DND_ITEM_TYPES.DESIGN_CARD,
    item: () => {
      const firstImageUrl = design?.images?.[0]?.url;
      const secondImageUrl = design?.images?.[1]?.url;
      return {
        type: DND_ITEM_TYPES.DESIGN_CARD,
        designId: design.id,
        designName: design.name,
        projectId: design.projectId,
        projectName: projectName,
        teamId: teamId || "",
        firstImageUrl: firstImageUrl,
        secondImageUrl: secondImageUrl,
      };
    },
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
    canDrag: !noNavigate && !!teamId && !disableDrag,
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const update = useCallback(async () => {
    await (onUpdate ? onUpdate(true) : refetchProject({ skipLoading: true }));
  }, [onUpdate, refetchProject]);

  const onFavoriteChange = async (_isFavorite: boolean) => {
    await DesignService.setFavoriteDesign(design.id, _isFavorite);

    await update();
  };

  const onDesignClick = () => {
    if (noNavigate) return;
    navigate(
      generatePath(ROUTES.STUDIO, {
        projectId: design.projectId,
        designId: design.id,
        teamId: teamId ?? "",
      })
    );
  };

  const productLine = useMemo(() => {
    return design?.productLine ?? ProductLineType.HYPER_CUSTOM;
  }, [design?.productLine]);

  return (
    <div
      ref={isMobile ? null : (drag as any)}
      style={{
        width: "100%",
        opacity: isDragging ? 0.4 : 1,
        cursor:
          !noNavigate && !!teamId && !disableDrag
            ? isDragging
              ? "grabbing"
              : "grab"
            : "default",
      }}
    >
      <Box
        sx={{
          ...styles.designContainer,
          cursor: !noNavigate && !!teamId && !disableDrag ? "grab" : "default",
          "&:active": {
            cursor:
              !noNavigate && !!teamId && !disableDrag ? "grabbing" : "default",
          },
        }}
        data-test={`designCard.${design.name}`}
      >
        <Box
          sx={{ ...styles.imgContainer, ...imgContainerStyles }}
          onClick={onDesignClick}
          flexGrow={1}
          onMouseEnter={() => setHoveringDesignContainer(true)}
          onMouseLeave={() => setHoveringDesignContainer(false)}
        >
          <Box sx={styles.img} display={"flex"} gap={1.5}>
            {design?.images?.map((image, index) => (
              <Box
                component={"img"}
                draggable={false}
                sx={{
                  ...styles.img,
                  content: `url(${image.url})`,
                  userSelect: "none",
                }}
                key={`${image.side}-${index}`}
              />
            ))}
          </Box>
          {isAdmin && !hideProductLine && (
            <Box sx={styles.productLineTag}>
              <Typography
                variant="textXxs"
                color={
                  ProductLinesToColor[
                    design?.productLine ?? ProductLineType.HYPER_CUSTOM
                  ]
                }
              >
                {t(`line.${productLine}.title`)}
              </Typography>
            </Box>
          )}
          {isHoveringDesignContainer && contentOnHover}
        </Box>
        {design.lockedVersion && !hideLocked && (
          <LockedDesign
            designId={design.id}
            locked={!!design.locked}
            lockedVersion={design.lockedVersion}
            onUpdate={update}
            customStyles={{ position: "absolute", top: "12px", left: "8px" }}
          />
        )}
        {!hideFavorite && (
          <Box
            sx={styles.favorite}
            data-test="designCard.favoriteIconContainer"
          >
            <Icon
              data-test="designCard.favoriteIcon"
              icon="favorite"
              filled={isFavorite}
              size={18}
              onClick={() =>
                setIsFavorite((prev) => {
                  onFavoriteChange(!prev);
                  return !prev;
                })
              }
            />
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          mb={"4px"}
          alignItems={"center"}
        >
          {!noNavigate ? (
            <Link
              to={
                teamId
                  ? generatePath(ROUTES.STUDIO, {
                      projectId: design.projectId,
                      designId: design.id,
                      teamId: teamId,
                    })
                  : ""
              }
              style={{
                maxWidth: "calc(100% - 2px - 8px)",
                display: "flex",
                color: palette.grey.nearBlack,
              }}
            >
              <Typography
                variant="textSm"
                sx={styles.designName}
                data-test="designCard.designName"
              >
                {design.name}
              </Typography>
            </Link>
          ) : (
            <Typography
              variant="textSm"
              sx={styles.designName}
              data-test="designCard.designName"
            >
              {design.name}
            </Typography>
          )}
          {!hideActions && (
            <Box>
              <Icon
                ref={ref}
                data-test="designCard.moreVertIcon"
                icon="more_vert"
                onClick={() => setIsContextMenuOpen((prev) => !prev)}
                style={{
                  width: "2px",
                  height: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
              <DesignCardMenu
                isOpen={isContextMenuOpen}
                onClose={() => setIsContextMenuOpen(false)}
                target={ref.current}
                design={design}
                onUpdate={update}
                setIsLoading={setIsLoading}
                projectName={projectName}
              />
            </Box>
          )}
        </Box>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={{ lg: spacing.xxsDesktop }}
          width={"100%"}
        >
          {!hideProjectName && !!projectName && !noNavigate && (
            <Link
              to={generatePath(ROUTES.PROJECT, {
                teamId,
                projectId: design.projectId,
              })}
              style={{
                color: palette.blue.secondary,
                display: "flex",
                maxWidth: "100%",
              }}
            >
              <Typography
                variant="textXs"
                sx={{
                  color: palette.blue.secondary,
                  "&:hover": { textDecoration: "underline" },
                }}
                maxWidth={"100%"}
                textOverflow={"ellipsis"}
                whiteSpace={"nowrap"}
                overflow={"hidden"}
                display={"block"}
              >
                {projectName}
              </Typography>
            </Link>
          )}
          {!isMobile && !hideTimeSince && (
            <Typography variant="textXs" maxWidth={"100%"}>
              {timeSince}
            </Typography>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default DesignCard;
